import { isTruthy } from '@ardoq/common-helpers';
import {
  LabelFormattingInfo,
  ComponentLabelSource,
  ReferenceLabelSource,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
} from '@ardoq/data-model';
import {
  selectOptionOperations,
  SelectOptionsOrGroups,
  SelectOption,
} from '@ardoq/select';
import {
  CommonButtonWithDropdownProps,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { v4 as uuidv4 } from 'uuid';
import { ButtonType } from '@ardoq/button';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';

export const getSelectedLabelFieldOptions = (
  labelFormatting: LabelFormattingInfo,
  fieldOptions: SelectOptionsOrGroups<string>
) => {
  const flatOptions =
    selectOptionOperations.optionsOrGroupsToFlatOptions(fieldOptions);
  return labelFormatting.fields
    .map(field => flatOptions.find(option => option.value === field))
    .filter(isTruthy);
};

export const getFieldOptionsWithDefault = (
  fieldOptions: SelectOption<string>[],
  isComponent: boolean
): SelectOptionsOrGroups<string> => {
  const defaultOptions = [
    isComponent
      ? { label: 'Component Type', value: ComponentLabelSource.TYPE }
      : { label: 'Display text', value: ReferenceLabelSource.DISPLAY_TEXT },
    {
      label: 'Description',
      value: COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
    },
  ];

  const defaultFields = {
    label: 'Default fields',
    options: defaultOptions,
  };

  return [
    defaultFields,
    {
      label: 'Custom fields',
      options: fieldOptions,
    },
  ];
};

type GetAddLabelButtonPropsArgs = {
  onLabelFormattingAdded: (labelFormatting: LabelFormattingInfo) => void;
  hasNoReferences: boolean;
  disableReferenceFormattingMessage?: string;
};

export const getAddLabelButtonProps = ({
  onLabelFormattingAdded,
  hasNoReferences,
  disableReferenceFormattingMessage,
}: GetAddLabelButtonPropsArgs): CommonButtonWithDropdownProps => {
  const onAddComponentLabelClick = () =>
    onLabelFormattingAdded({
      type: 'component',
      fields: [],
      showFieldName: true,
      // Type name will be piped to the initial value of the type select, null means All Types
      typeName: null,
      __id: uuidv4(),
    });

  const isAddingReferenceDisabled = Boolean(
    disableReferenceFormattingMessage || hasNoReferences
  );

  const onAddReferenceLabelClick = isAddingReferenceDisabled
    ? undefined
    : () =>
        onLabelFormattingAdded({
          type: 'reference',
          fields: [],
          showFieldName: true,
          // Type name will be piped to the initial value of the type select, null means All Types
          typeName: null,
          __id: uuidv4(),
        });

  return {
    buttonType: ButtonType.TERTIARY,
    dropdownSize: DropdownSize.S,
    options: [
      {
        label: 'Component label',
        type: DropdownOptionType.OPTION,
        onClick: onAddComponentLabelClick,
      },
      {
        label: 'Reference label',
        type: DropdownOptionType.OPTION,
        onClick: onAddReferenceLabelClick,
        isDisabled: isAddingReferenceDisabled,
        popoverProps: isAddingReferenceDisabled
          ? {
              content:
                disableReferenceFormattingMessage ??
                'There are no references in the opened datasets. Try editing your datasets or open more data.',
            }
          : undefined,
      },
    ],
  };
};

export const getLabelFormattingTypeLabel = (
  labelFormatting: LabelFormattingInfo,
  typeOptions: SelectOptionsOrGroups<string | null>
) =>
  selectOptionOperations
    .optionsOrGroupsToFlatOptions(typeOptions)
    .find(option => option.value === labelFormatting.typeName)?.label;
